import { inject, Injectable } from '@angular/core';
import { InjectableSuperclass } from '@sama/ng-core';
import { EventTrackingValueWithSource } from '../../../../../cross-team-shared/misc-utils';
import { ViewMode } from '../../../../../cross-team-shared/state/abstract-sensor-content-state';
import { CalibrationOptions } from '../../../../../cross-team-shared/state/calibration-options-state';
import { EventTrackingService } from '../../../../../shared/event-tracking/event-tracking.service';
import { toggleState } from '../../../../../shared/misc-utilities/store-utils';
import { FusionManagerFactory } from '../../../../outputs/annotation/annotation-shared/fusion/fusion-manager-factory.service';
import { TaskWorkspaceStore } from '../../../state/task-workspace-store';
import { getOutputByName } from '../sensor-fusion-output.utils';

@Injectable()
export class SettingsService extends InjectableSuperclass {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private eventTrackingService = inject(EventTrackingService);
  private fusionManagerFactory = inject(FusionManagerFactory);
  private settingsStore = this.taskWorkspaceStore('settings');
  private viewModes: ViewMode[] = ['all', 'selected', 'none'];

  setCalibrationOptions(
    outputName: string,
    calibrationOptions: CalibrationOptions,
  ) {
    this.settingsStore('calibrationOptions')(outputName).set(
      calibrationOptions,
    );
  }

  updateCalibrationOptions(
    outputName: string,
    calibrationOptions: Partial<CalibrationOptions>,
  ) {
    this.settingsStore('calibrationOptions')(outputName).assign(
      calibrationOptions,
    );
  }

  toggleZoomPanel(method: 'button' | 'shortcut') {
    toggleState(this.settingsStore('zoomPanel')('isOpen'));
    this.eventTrackingService.track({
      id: 'toggle_zoom_panel',
      bucket: 'navigation',
      sub_bucket: 'frame',
      new_value: true,
      method,
    });
  }

  toggleViewMode(eventTrackingValue?: EventTrackingValueWithSource) {
    const currentViewModeIndex = this.viewModes.findIndex(
      (viewMode) => viewMode === this.settingsStore.state().viewMode,
    );
    const nextViewModeIndex =
      currentViewModeIndex === this.viewModes.length - 1
        ? 0
        : currentViewModeIndex + 1;
    this.setViewMode(this.viewModes[nextViewModeIndex], eventTrackingValue);
  }

  setViewMode(
    viewMode: ViewMode,
    eventTrackingValue?: EventTrackingValueWithSource,
  ) {
    this.settingsStore('viewMode').set(viewMode);
    this.eventTrackingService.track({
      id: 'set_view_mode',
      bucket: 'navigation',
      sub_bucket: 'frame',
      ...eventTrackingValue,
      method: viewMode,
    });
  }

  toggleHideGround() {
    toggleState(this.settingsStore('hideGround'));
    this.eventTrackingService.track({
      id: 'toggle_show_ground',
      bucket: 'navigation',
      sub_bucket: 'frame',
      new_value: !this.settingsStore.state().hideGround,
    });
  }

  toggleShowRoute() {
    toggleState(this.settingsStore('showRoute'));
    this.eventTrackingService.track({
      id: 'toggle_show_route',
      bucket: 'navigation',
      sub_bucket: 'frame',
      new_value: this.settingsStore.state().showRoute,
    });
  }

  toggleOrthographicCamera(
    eventTrackingValueWithSource: EventTrackingValueWithSource,
  ) {
    toggleState(this.settingsStore('useOrthographicCamera'));
    this.trackMainCameraTypeEvent(eventTrackingValueWithSource);
  }

  toggleInspectMode(
    eventTrackingValueWithSource: EventTrackingValueWithSource,
  ) {
    toggleState(this.settingsStore('inspectMode'));
    this.eventTrackingService.track({
      id: 'toggle_inspect_mode',
      bucket: 'navigation',
      sub_bucket: 'frame',
      ...eventTrackingValueWithSource,
      new_value: this.settingsStore.state().inspectMode,
    });
  }

  trackMainCameraTypeEvent(
    eventTrackingValueWithSource: EventTrackingValueWithSource,
  ) {
    this.eventTrackingService.track({
      id: 'toggle_main_camera_type',
      bucket: 'navigation',
      sub_bucket: 'frame',
      new_value: this.settingsStore.state().useOrthographicCamera
        ? 'orthographic'
        : 'perspective',
      ...eventTrackingValueWithSource,
    });
  }

  resetSensorView(eventTrackingValueWithSource: EventTrackingValueWithSource) {
    const selectedOutputName =
      this.taskWorkspaceStore.state().selectedOutputName;
    const output = getOutputByName(
      this.taskWorkspaceStore.state().project,
      selectedOutputName,
    );

    this.fusionManagerFactory
      .getManager(output.id)
      .getMinionByName(selectedOutputName)
      ?.contentConnector.resetSensorView(eventTrackingValueWithSource);
  }

  toggleEnhancedPointCloud(
    eventTrackingValueWithSource: EventTrackingValueWithSource,
  ) {
    toggleState(this.settingsStore('enhancedCloudSettings')('enabled'));
    this.eventTrackingService.track({
      id: 'toggle_enhanced_point_cloud',
      bucket: 'navigation',
      sub_bucket: 'frame',
      new_value: this.settingsStore.state().enhancedCloudSettings.enabled,
      ...eventTrackingValueWithSource,
    });
  }

  toggleShowCloudProjection() {
    toggleState(this.settingsStore('showCloudProjection'));
    this.eventTrackingService.track({
      id: 'change_projection_view',
      bucket: 'navigation',
      sub_bucket: 'frame',
      projection_type: 'point_cloud',
      active: this.settingsStore.state().showCloudProjection,
    });
  }

  toggleShowShapeProjection() {
    toggleState(this.settingsStore('showShapeProjection'));
    this.eventTrackingService.track({
      id: 'change_projection_view',
      bucket: 'navigation',
      sub_bucket: 'frame',
      projection_type: 'shapes',
      active: this.settingsStore.state().showShapeProjection,
    });
  }

  setViewModeOptions(viewModeOptions: ViewMode[]) {
    this.viewModes = viewModeOptions;
  }
}
